import {
  isValidElement,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  FormattedMessage,
  type IntlShape,
  type MessageDescriptor,
} from 'react-intl';

import type { AriaLabelingProps } from '../../../core/types/a11y-props.js';
import { isSlot } from '../../../core/utils/is-slot.js';

function isFormattedMessageElement(
  node: ReactNode,
  // Needs to overlap with the types from the FormattedMessage function
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): node is ReactElement<MessageDescriptor & { values: Record<string, any> }> {
  return isSlot(node, FormattedMessage);
}

/**
 * This extracts the text content from a `ReactNode` label. If there are no text children - e.g. the user defined their own component
 * and passes it to the label like this: `label={<ComplexLabel/>}` - then the name is used as fallback.
 * The name is the unique identifier of each item and must be a string.
 */
export const getTextFromNode: (
  node: ReactNode,
  intl: IntlShape,
  name: string,
) => string = (children, intl, name) => {
  if (!children) {
    return '';
  }

  if (typeof children === 'string') {
    return children;
  }

  if (typeof children === 'number') {
    return children.toString();
  }

  if (isValidElement<PropsWithChildren<AriaLabelingProps>>(children)) {
    if (children.props['aria-label']) {
      return children.props['aria-label'];
    }

    if (children.props.children) {
      return getTextFromNode(children.props.children, intl, name);
    }

    if (isFormattedMessageElement(children)) {
      return intl.formatMessage(children.props);
    }
  }

  return Array.isArray(children)
    ? children
        .map((child) => getTextFromNode(child, intl, name).trimEnd()) // the empty spaces are trimmed at the end because we add a whitespace when joining multiple text nodes (e.g. Country <Emphasis>Office</Emphasis>)
        .join(' ')
    : name;
};
